import nanoid from 'nanoid';
import { format } from 'date-fns';

import { ClaimType, DamageDescription, MissingItemDescription, ClaimContext } from './types';
import { ClaimRequirements, ClaimTypeRequirements, ConsumerActionStatus } from '../../../api/types';
import { ClaimContextForAdditionalInfoStep } from './common/AdditionalInfoStep';
import { ClaimShopSettings } from '../ClaimsShopSettingsContext';

export const currencies = ['EUR', 'CHF', 'CZK', 'DKK', 'GBP', 'HUF', 'NOK', 'PLN', 'RON', 'SEK'];

const countryCurrencies: Record<string, string | undefined> = {
  li: 'CHF',
  cz: 'CZK',
  dk: 'DKK',
  gb: 'GBP',
  hu: 'HUF',
  no: 'NOK',
  pl: 'PLN',
  ro: 'RON',
  se: 'SEK',
};

export const languageLabels: Record<string, string> = {
  en: 'English',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  nl: 'Dutch',
  it: 'Italian',
  pl: 'Polish',
  pt: 'Portuguese',
};

export function createEmptyInfoItem(): MissingItemDescription {
  return {
    id: nanoid(5),
    amount: 1,
    itemName: '',
  };
}

export function createEmptyDamageDescription(): DamageDescription {
  return {
    id: nanoid(5),
    amount: 1,
    itemName: '',
    damageDescription: '',
  };
}

export function formatDate(date: Date | string): string {
  return format(date, 'dd.MM.yyyy');
}

export function getCurrencyOfCountry(countryCode?: string | null): string {
  return countryCurrencies[countryCode || ''] || 'EUR';
}

export function isWarehouseDeliveryDateRequired(claim: ClaimContext) {
  const { useWarehouseDelivery, outcome } = getDeadline(claim) || {};

  if (!useWarehouseDelivery) {
    return false;
  }

  return outcome === 'early' || outcome === 'past';
}

export function isNonManual(claim: Pick<ClaimContext, 'shipmentId'>) {
  return !!claim.shipmentId;
}

function getDeadline(claim: ClaimContext) {
  return claim.submissionInfo && claim.type ? claim.submissionInfo[claim.type].deadline : null;
}

export function getClaimableWarning(claim: Pick<ClaimContext, 'submissionInfo' | 'type'>) {
  return claim.submissionInfo && claim.type ? claim.submissionInfo[claim.type].warning : null;
}

export function isClaimWithMissingTrackingData(
  claim: Pick<ClaimContext, 'submissionInfo' | 'type' | 'shipmentId' | 'entryPoint'>
) {
  return getClaimableWarning(claim) === 'no_required_tracking_data';
}

export function isManualDraft(claim: Pick<ClaimContext, 'shipmentId' | 'draftId'>) {
  return claim.draftId && !claim.shipmentId;
}

export function isClaimOutsideOfDeadline(claim: ClaimContext) {
  const { outcome } = getDeadline(claim) || {};
  // it's better to not compare against "within" because for manual flow we don't have deadlines
  return outcome === 'early' || outcome === 'past';
}

export function isClaimWithMailboxDeliveryWarning(claim: ClaimContext) {
  return getClaimableWarning(claim) === 'mailbox_delivery_no_liability';
}

export function isShipmentDelivered(claim: ClaimContext) {
  return claim.isDelivered;
}

export function isInvestigationClaim(claim: ClaimContext) {
  return claim.type === 'investigation';
}

/**
 * Special investigation submission flow where the claim can only be saved as a draft at the end
 * and some validation rules change during submission
 */
export function isSpecialInvestigationFlow(context: Pick<ClaimContext, 'shopSettings' | 'type'>) {
  return context.type === ClaimType.INVESTIGATION && !!context.shopSettings?.isSpecialInvestigationFlowEnabled;
}

export type ClaimContextForSpecialInvestigationFlowCompleteness = ClaimContextForAdditionalInfoStep &
  Pick<ClaimContext, 'invoiceDocuments' | 'netValue' | 'orderItems' | 'isOrderItemsRequired'>;

/**
 * Whether fields that are optional in special flow are all filled
 * If they are, we want to display submit claim button instead of save draft
 */
export function isSpecialInvestigationFlowClaimComplete(context: ClaimContextForSpecialInvestigationFlowCompleteness) {
  const { declarationOfRecipientRequirement, isDataProcessingConsentRequired } =
    computeAdditionalInfoRequirements(context);

  const hasInvoice = context.invoiceDocuments.length > 0;
  const hasNetValue = !!context.netValue;
  const hasOrderItems = !context.isOrderItemsRequired || context.orderItems.length > 0;
  const hasDOR = declarationOfRecipientRequirement !== 'required' || context.declarationOfRecipient.length > 0;
  const hasDataProcessingConsent = !isDataProcessingConsentRequired || context.dataProcessingConsent.length > 0;

  return hasInvoice && hasNetValue && hasOrderItems && hasDOR && hasDataProcessingConsent;
}

export function isPartialDamageClaim(claim: ClaimContext) {
  return claim.type === 'partial_damage';
}

export function isWholeDamageClaim(claim: ClaimContext) {
  return claim.type === 'whole_damage';
}

export function isMissingItemClaim(claim: ClaimContext) {
  return claim.type === 'missing_item';
}

export function isCashOnDeliveryClaim(claim: ClaimContext) {
  return claim.type === 'cash_on_delivery';
}

export function isClaimsCenter(claim: Pick<ClaimContext, 'entryPoint'>) {
  return claim.entryPoint === 'claims-center';
}

export function isManualClaim(claim: Pick<ClaimContext, 'entryPoint' | 'shipmentId'>) {
  return isClaimsCenter(claim) && !isNonManual(claim);
}

export function isClaimsCenterAndNonManual(claim: ClaimContext) {
  return isClaimsCenter(claim) && isNonManual(claim);
}

export function isAdditionalInfoRequired(context: ClaimContext) {
  const { isAdditionalInfoRequired } = computeAdditionalInfoRequirements(context);
  return isAdditionalInfoRequired;
}

export function isInvoiceRequired(context: Pick<ClaimContext, 'type'>) {
  return context.type !== ClaimType.CASH_ON_DELIVERY;
}

// returns the requirements matrix entry for the active claim type
// (cash_on_delivery claims have no matrix entry, and manual claims have no requirements before they are fetched)
export function getClaimTypeRequirements(
  claimRequirements: ClaimRequirements | null | undefined,
  type: ClaimType | undefined
): ClaimTypeRequirements | undefined {
  if (!claimRequirements || !type || type === ClaimType.CASH_ON_DELIVERY) {
    return undefined;
  }

  return claimRequirements[type];
}

export function getIsOrderItemsTaxRate(
  claimRequirements: ClaimRequirements | null | undefined,
  shopSettings: Pick<ClaimShopSettings, 'isOrderItemLevelTaxEnabled'>
): boolean {
  // isOrderItemsTaxRate has the same value for every claim type in the requirements matrix,
  // the shop settings flag is kept as a fallback for contexts created before requirements are available
  return claimRequirements?.[ClaimType.INVESTIGATION]?.isOrderItemsTaxRate ?? shopSettings.isOrderItemLevelTaxEnabled;
}

export function computeAdditionalInfoRequirements(context: ClaimContextForAdditionalInfoStep) {
  const typeRequirements = getClaimTypeRequirements(context.claimRequirements, context.type);

  const isMissingItemClaim = !!context.type && context.type === ClaimType.MISSING_ITEM;
  const isDamageInfoRequired =
    !!context.type && [ClaimType.PARTIAL_DAMAGE, ClaimType.WHOLE_DAMAGE].includes(context.type);
  const isCashOnDeliveryClaim = context.type === ClaimType.CASH_ON_DELIVERY;

  // 'optional' renders the input without required marker/validation (e.g. DHL-DE), 'none' hides it
  const carrierCaseIdRequirement = typeRequirements?.carrierCaseId ?? 'none';
  const carrierDamageReportRequirement = typeRequirements?.carrierDamageReport ?? 'none';

  const declarationOfRecipientRequirement = typeRequirements?.declarationOfRecipient ?? 'none';

  // uses the legacy top-level field on purpose: the matrix has no cash_on_delivery entry,
  // but consent can be required for cash on delivery claims as well
  const isDataProcessingConsentRequired =
    context.claimRequirements?.dataProcessingConsent === 'required' && declarationOfRecipientRequirement === 'none';

  const isPicturesOfMissingItemRequired = typeRequirements?.picturesOfMissingItem === 'required' && isMissingItemClaim;

  const isDamagePhotosRequired = typeRequirements?.picturesOfDamage === 'required' && isDamageInfoRequired;

  const isPictureUploadLinkStepRequired = isPictureUploadLinkStepEnabled(context);

  // the matrix already encodes claim-type gating, but we keep suppressing
  // the carrier-specific inputs for cash on delivery claims on the client
  const isWeightRequired = typeRequirements?.weight === 'required' && !isCashOnDeliveryClaim;
  const isRecipientPhoneNumberRequired =
    typeRequirements?.recipientPhoneNumber === 'required' && !isCashOnDeliveryClaim;

  const isCarrierSpecificDataRequired =
    isWeightRequired ||
    isRecipientPhoneNumberRequired ||
    carrierCaseIdRequirement !== 'none' ||
    carrierDamageReportRequirement !== 'none' ||
    isDataProcessingConsentRequired;

  const isAdditionalInfoRequired =
    declarationOfRecipientRequirement !== 'none' ||
    isPictureUploadLinkStepRequired ||
    isCarrierSpecificDataRequired ||
    isDamageInfoRequired ||
    isCashOnDeliveryClaim ||
    isMissingItemClaim;

  return {
    isWeightRequired,
    isRecipientPhoneNumberRequired,
    isPicturesOfMissingItemRequired,
    isDamagePhotosRequired,
    carrierCaseIdRequirement,
    carrierDamageReportRequirement,
    isPictureUploadLinkStepRequired,
    isCarrierSpecificDataRequired,
    isAdditionalInfoRequired,
    isDamageInfoRequired,
    declarationOfRecipientRequirement,
    isDataProcessingConsentRequired,
  };
}

export function isPictureUploadLinkStepEnabled(
  context: Pick<ClaimContext, 'shopSettings' | 'picturesOfDamage' | 'picturesOfMissingItem' | 'type' | 'shipmentId'>
) {
  return (
    context.type &&
    [ClaimType.PARTIAL_DAMAGE, ClaimType.WHOLE_DAMAGE, ClaimType.MISSING_ITEM].includes(context.type) &&
    context.shopSettings.isPhotoCollectionAppEnabled &&
    !!context.shipmentId
  );
}

export function isDorSigningLinkStepEnabled(context: ClaimContext) {
  return (
    (context.shouldForceShowEDORStep || context.consumerActionStatus !== ConsumerActionStatus.COMPLETED) &&
    isShipmentDelivered(context) &&
    context.shopSettings.isDORSigningLinkEnabled &&
    isNonManual(context) &&
    (context.claimRequirements?.declarationOfRecipient === 'required' ||
      context.claimRequirements?.declarationOfRecipient === 'optional')
  );
}
